import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, Clock, Share2, XCircle } from 'lucide-react';
import { rupees, shopApi, tokenFor, type PublicOrder } from './shopApi';
import { LookupForm } from './OrdersPage';
import { UpiQrPay } from './UpiQrPay';

const STEPS = ['Placed', 'Payment verified', 'Confirmed', 'Out for delivery', 'Delivered'] as const;

function reached(o: PublicOrder): number {
  if (o.status === 'Delivered') return 4;
  if (o.status === 'Out for delivery') return 3;
  if (o.status === 'Confirmed') return 2;
  if (o.payment_status === 'Verified') return 1;
  return 0;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

function PayBox({ order, token }: { order: PublicOrder; token: string }) {
  const qc = useQueryClient();
  return (
    <UpiQrPay
      link={order.upi_link!} amount={order.total} payee={order.payee_name} upiId={order.upi_id} reference={`Order ${order.ref}`}
      notReceived={order.payment_status === 'Not received'}
      onPaid={async (utr) => qc.setQueryData(['shop-order', order.ref], await shopApi.paid(order.ref, token, utr))}
    />
  );
}

export default function OrderPage() {
  const { ref = '' } = useParams();
  const qc = useQueryClient();
  const token = tokenFor(ref);
  const q = useQuery({
    queryKey: ['shop-order', ref],
    queryFn: () => shopApi.get(ref, token!),
    enabled: !!token,
    // Keep checking while the farm still has something to do.
    refetchInterval: (query) => {
      const o = query.state.data as PublicOrder | undefined;
      return o && (o.status === 'Delivered' || o.status === 'Cancelled') ? false : 20_000;
    },
  });
  const cancel = useMutation({
    mutationFn: () => shopApi.cancel(ref, token!),
    onSuccess: (o) => qc.setQueryData(['shop-order', ref], o),
  });

  if (!token) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-xl font-bold text-brand-green-900">Order {ref}</h1>
        <p className="text-sm text-ink-500">To see this order on this phone, enter its order number and the mobile number used.</p>
        <LookupForm initialRef={ref} />
      </div>
    );
  }
  if (q.isLoading) return <div className="h-64 animate-pulse rounded-xl bg-white" />;
  if (q.isError || !q.data) {
    return (
      <div className="flex flex-col gap-3">
        <p className="rounded-lg bg-white p-4 text-sm text-ink-500">This order link has expired on this phone. Find it again:</p>
        <LookupForm initialRef={ref} />
      </div>
    );
  }
  const o = q.data;
  const step = reached(o);
  const cancelled = o.status === 'Cancelled';
  const share = () => {
    const text = `My Sri KalpaGavya order ${o.ref} — ${rupees(o.total)}`;
    if (navigator.share) navigator.share({ title: 'My order', text, url: window.location.href }).catch(() => undefined);
    else navigator.clipboard?.writeText(`${text} ${window.location.href}`);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-ink-400">Order number</p>
          <h1 className="text-xl font-bold tracking-wide text-brand-green-900">{o.ref}</h1>
          <p className="text-xs text-ink-500">Placed {when(o.created_at)}</p>
        </div>
        <button type="button" onClick={share} className="inline-flex items-center gap-1.5 rounded-full border border-brand-green-200 bg-white px-3 py-1.5 text-xs font-semibold text-brand-green-800">
          <Share2 className="h-3.5 w-3.5" /> Share
        </button>
      </div>

      {cancelled ? (
        <div className="flex gap-2 rounded-xl bg-critical-50 p-3 text-sm text-critical-700"><XCircle className="h-5 w-5 shrink-0" />
          <span><b>Cancelled.</b> {o.cancel_reason}{o.payment_status === 'Refunded' ? ' Your money has been refunded.' : ''}</span></div>
      ) : o.payment_status === 'Paid - to verify' ? (
        <div className="flex gap-2 rounded-xl bg-warning-50 p-3 text-sm text-warning-700"><Clock className="h-5 w-5 shrink-0" />
          <span><b>Payment sent — we're checking it.</b> UPI reference {o.utr}. This page updates by itself once we confirm.</span></div>
      ) : o.status === 'Delivered' ? (
        <div className="flex gap-2 rounded-xl bg-success-50 p-3 text-sm text-success-700"><CheckCircle2 className="h-5 w-5 shrink-0" /><span><b>Delivered.</b> Thank you for ordering!</span></div>
      ) : step >= 2 ? (
        <div className="flex gap-2 rounded-xl bg-success-50 p-3 text-sm text-success-700"><CheckCircle2 className="h-5 w-5 shrink-0" />
          <span><b>{o.status === 'Out for delivery' ? 'On the way!' : 'Confirmed.'}</b> Delivery {new Date(`${o.delivery_date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}, {o.delivery_slot}.</span></div>
      ) : o.payment_method === 'Cash' ? (
        <div className="flex gap-2 rounded-xl bg-info-50 p-3 text-sm text-info-700"><Clock className="h-5 w-5 shrink-0" /><span>Order received — we'll confirm it soon. Pay {rupees(o.total)} in cash on delivery.</span></div>
      ) : null}

      {o.can_pay && o.upi_link && <PayBox order={o} token={token} />}

      {!cancelled && (
        <section className="rounded-xl bg-white p-4">
          <p className="mb-2 text-sm font-semibold">Order status</p>
          <ol className="flex flex-col">
            {STEPS.filter((s) => !(s === 'Payment verified' && o.payment_method === 'Cash')).map((s) => {
              const idx = STEPS.indexOf(s);
              const done = idx <= step;
              return (
                <li key={s} className="flex items-center gap-2.5 py-1.5 text-sm">
                  {done ? <CheckCircle2 className="h-5 w-5 text-success-600" /> : <Circle className="h-5 w-5 text-ink-300" />}
                  <span className={done ? 'font-semibold text-ink-900' : 'text-ink-400'}>{s}</span>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <section className="rounded-xl bg-white p-4 text-sm">
        <p className="mb-1 font-semibold">{o.customer_name} · {o.mobile_masked}</p>
        <p className="text-ink-500">{o.address}</p>
        <p className="text-ink-500">Delivery: {new Date(`${o.delivery_date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })} · {o.delivery_slot}</p>
        <div className="mt-3 border-t border-ink-300/20 pt-2">
          {o.items.map((i, n) => (
            <div key={n} className="flex justify-between py-0.5"><span>{i.name} {i.pack_label} × {i.quantity}</span><span className="tabular-nums">{rupees(i.line_total)}</span></div>
          ))}
          <div className="flex justify-between py-0.5 text-ink-500"><span>Delivery</span><span>{Number(o.delivery_fee) ? rupees(o.delivery_fee) : 'Free'}</span></div>
          <div className="flex justify-between border-t border-dashed border-ink-300/50 pt-1.5 font-bold"><span>Total</span><span>{rupees(o.total)}</span></div>
          <p className="mt-1 text-xs text-ink-500">Payment: {o.payment_method} · {o.payment_status}</p>
        </div>
      </section>

      {o.timeline.length > 0 && (
        <section className="rounded-xl bg-white p-4">
          <p className="mb-2 text-sm font-semibold">Updates</p>
          <ul className="flex flex-col gap-2">
            {[...o.timeline].reverse().map((e, i) => (
              <li key={i} className="text-sm">
                <span className="font-medium">{e.label}</span> <span className="text-xs text-ink-400">· {when(e.created_at)}</span>
                {e.note && <p className="text-xs text-ink-500">{e.note}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {o.can_cancel && (
        <button type="button" onClick={() => { if (window.confirm('Cancel this order?')) cancel.mutate(); }}
          className="text-sm font-medium text-critical-600">
          {cancel.isPending ? 'Cancelling…' : 'Cancel this order'}
        </button>
      )}
      <Link to="/shop" className="text-center text-sm font-semibold text-brand-green-700">← Back to the shop</Link>
    </div>
  );
}
